/**
 * TemplatesPage — `/templates` dedicated browse / rename / delete / duplicate UI.
 *
 * Backs onto the 4 /api/brand/wave-templates endpoints shipped in iter 56
 * plus the new PATCH /rename endpoint added here.
 */
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import DashboardLayout from '../layouts/DashboardLayout';
import { Bookmark, Copy, Trash2, Edit3, Waves, Loader2, Check, Plus, Archive, Sparkles, ArrowRight } from 'lucide-react';
import { useDemoProfile } from '../hooks/useDemoProfile';

const API = process.env.REACT_APP_BACKEND_URL;

export default function TemplatesPage() {
  const navigate = useNavigate();
  const [templates, setTemplates] = useState(null);
  const [busyId, setBusyId] = useState(null);
  const [toast, setToast] = useState(null);
  const { profile, isDemo } = useDemoProfile();
  // Demo brands get a curated set of proven wave patterns to clone from
  // without having to save any templates first. Stored on the demo profile.
  const suggestedPatterns = (isDemo && profile?.wave_library_templates) || [];

  const load = async () => {
    try {
      const r = await axios.get(`${API}/api/brand/wave-templates`, { withCredentials: true });
      setTemplates(r.data || []);
    } catch {
      setTemplates([]);
    }
  };
  useEffect(() => { load(); }, []);

  const rename = async (t) => {
    const next = window.prompt('Rename template', t.name || '');
    if (!next || next.trim() === t.name) return;
    setBusyId(t.id);
    try {
      await axios.patch(`${API}/api/brand/wave-templates/${t.id}`, { name: next.trim() }, { withCredentials: true });
      await load();
      flashToast('Renamed', 'ok');
    } catch (e) {
      flashToast(e?.response?.data?.detail || 'Rename failed', 'err');
    } finally { setBusyId(null); }
  };

  const duplicate = async (t) => {
    setBusyId(t.id);
    try {
      await axios.post(`${API}/api/brand/wave-templates/${t.id}/duplicate`, {}, { withCredentials: true });
      await load();
      flashToast('Duplicated', 'ok');
    } catch (e) {
      flashToast(e?.response?.data?.detail || 'Duplicate failed', 'err');
    } finally { setBusyId(null); }
  };

  const clone = async (t) => {
    setBusyId(t.id);
    try {
      const r = await axios.post(`${API}/api/brand/wave-templates/${t.id}/clone`, {}, { withCredentials: true });
      if (r.data?.id) navigate(`/waves/${r.data.id}/control`);
    } catch (e) {
      flashToast(e?.response?.data?.detail || 'Clone failed', 'err');
      setBusyId(null);
    }
  };

  const remove = async (t) => {
    if (!window.confirm(`Delete template "${t.name}"? This cannot be undone.`)) return;
    setBusyId(t.id);
    try {
      await axios.delete(`${API}/api/brand/wave-templates/${t.id}`, { withCredentials: true });
      await load();
      flashToast('Deleted', 'ok');
    } catch (e) {
      flashToast(e?.response?.data?.detail || 'Delete failed', 'err');
    } finally { setBusyId(null); }
  };

  const flashToast = (msg, type) => {
    setToast({ msg, type });
    setTimeout(() => setToast(null), 2600);
  };

  return (
    <DashboardLayout>
      <div className="p-5 lg:p-8 max-w-[1200px] mx-auto" data-testid="templates-page">
        <header className="mb-8">
          <p className="text-[10px] uppercase tracking-[0.25em] text-[#FF4D94] font-bold">Wave Library</p>
          <h1 className="text-3xl sm:text-4xl font-black text-white tracking-tight mt-2">Saved wave templates.</h1>
          <p className="text-[12px] text-gray-500 mt-2 max-w-prose leading-relaxed">
            Reusable planning patterns saved from your finished waves. Clone into a fresh draft to reuse the creator roles, burst plan, and KPI benchmarks without rebuilding from scratch.
          </p>
        </header>

        {/* Suggested wave patterns — only for demo brands. Cloneable starting
            points modeled on the brand's most successful past waves. */}
        {suggestedPatterns.length > 0 && (
          <section className="mb-10" data-testid="suggested-wave-patterns">
            <div className="flex items-baseline justify-between mb-4">
              <h2 className="text-[10px] uppercase tracking-[0.22em] font-black text-[#00D4FF] flex items-center gap-1.5">
                <Sparkles className="h-3 w-3" /> Suggested Wave Patterns
              </h2>
              <span className="text-[10px] text-gray-600">{suggestedPatterns.length} proven playbooks</span>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
              {suggestedPatterns.map((p, i) => (
                <button
                  type="button"
                  key={`${p.name}-${i}`}
                  onClick={() => navigate(`/build-wave?tentpole=${encodeURIComponent(p.tentpole || '')}`)}
                  data-testid={`pattern-card-${i}`}
                  className="text-left rounded-xl p-4 group hover:bg-[#161616] transition border"
                  style={{ background: '#121212', borderColor: '#1f1f1f' }}
                >
                  <p className="text-[9px] uppercase tracking-[0.18em] font-bold text-[#00D4FF] mb-1">
                    {p.tentpole}
                  </p>
                  <h3 className="text-[13px] font-semibold text-white leading-snug mb-2">
                    {p.name}
                  </h3>
                  <p className="text-[11px] text-gray-400 leading-relaxed line-clamp-3 mb-3">
                    {p.desc}
                  </p>
                  <div className="flex items-center justify-between text-[10px]">
                    <span className="text-gray-500">
                      {p.creators} creators · {p.budget} · {p.duration}
                    </span>
                    <span className="inline-flex items-center gap-1 text-[#00D4FF] font-bold uppercase tracking-wider">
                      Clone <ArrowRight className="h-3 w-3" />
                    </span>
                  </div>
                  {(p.platforms || []).length > 0 && (
                    <div className="flex flex-wrap gap-1 mt-2.5 pt-2.5 border-t border-[#1f1f1f]">
                      {p.platforms.map(pl => (
                        <span key={pl} className="text-[9px] uppercase tracking-widest text-gray-500 border border-[#1f1f1f] rounded-full px-1.5 py-0.5">
                          {pl}
                        </span>
                      ))}
                    </div>
                  )}
                </button>
              ))}
            </div>
          </section>
        )}

        {templates === null ? (
          <div className="py-16 text-center text-gray-500 text-sm">
            <Loader2 className="h-4 w-4 animate-spin inline mr-2" /> Loading library…
          </div>
        ) : templates.length === 0 ? (
          suggestedPatterns.length > 0 ? (
            <div className="rounded-xl p-5 text-center text-[12px] text-gray-500"
                 style={{ background: '#0F0F0F', border: '1px dashed #1f1f1f' }}
                 data-testid="user-templates-empty-with-patterns">
              No personal templates yet. Save any wave from its Control Room to add a custom one to this list.
            </div>
          ) : (
            <EmptyState onPrimary={() => navigate('/campaigns')} />
          )
        ) : (
          <ul className="space-y-3" data-testid="templates-list">
            {templates.map(t => (
              <li key={t.id}
                  className="rounded-xl p-5 flex items-start gap-4 flex-wrap sm:flex-nowrap"
                  style={{ background: '#0F0F0F', border: '1px solid #1f1f1f' }}
                  data-testid={`template-card-${t.id}`}>
                <div className="flex-shrink-0 h-10 w-10 rounded-lg flex items-center justify-center" style={{ background: '#FF4D9420' }}>
                  <Bookmark className="h-4 w-4 text-[#FF4D94]" />
                </div>
                <div className="flex-1 min-w-[200px]">
                  <h3 className="text-sm font-black text-white tracking-tight">{t.name}</h3>
                  {t.description && <p className="text-[11px] text-gray-400 mt-0.5 leading-relaxed">{t.description}</p>}
                  <div className="flex flex-wrap gap-2 mt-2 text-[10px] text-gray-500">
                    {t.source_wave_name && (
                      <span className="inline-flex items-center gap-1">
                        <Waves className="h-3 w-3 text-[#00D4FF]" /> From: <span className="text-gray-300">{t.source_wave_name}</span>
                      </span>
                    )}
                    <span>· {t.field_count} fields</span>
                    <span>· Used {t.use_count}×</span>
                    <span>· {t.created_at ? new Date(t.created_at).toLocaleDateString() : ''}</span>
                  </div>
                </div>
                <div className="flex items-center gap-1.5">
                  <ActionPill label="Clone"     icon={Waves}   testId={`clone-${t.id}`}     onClick={() => clone(t)}     busy={busyId === t.id} primary />
                  <ActionPill label="Duplicate" icon={Copy}    testId={`duplicate-${t.id}`} onClick={() => duplicate(t)} busy={busyId === t.id} />
                  <ActionPill label="Rename"    icon={Edit3}   testId={`rename-${t.id}`}    onClick={() => rename(t)}    busy={busyId === t.id} />
                  <ActionPill label="Delete"    icon={Trash2}  testId={`delete-${t.id}`}    onClick={() => remove(t)}    busy={busyId === t.id} danger />
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>

      {toast && (
        <div className="fixed bottom-6 right-6 z-50 rounded-lg px-4 py-3 text-[12px] font-bold"
             style={{
               background: toast.type === 'ok' ? '#4ade8020' : '#FF6B6B20',
               border: `1px solid ${toast.type === 'ok' ? '#4ade80' : '#FF6B6B'}`,
               color: toast.type === 'ok' ? '#4ade80' : '#FF6B6B',
             }}
             data-testid="templates-toast">
          {toast.type === 'ok' ? <Check className="h-3 w-3 inline mr-1.5" /> : null}
          {toast.msg}
        </div>
      )}
    </DashboardLayout>
  );
}

function EmptyState({ onPrimary }) {
  return (
    <div className="rounded-2xl py-16 px-6 text-center"
         style={{ background: '#0F0F0F', border: '1px dashed #1f1f1f' }}
         data-testid="templates-empty">
      <Archive className="h-8 w-8 text-gray-600 mx-auto mb-3" />
      <p className="text-base font-black text-white mb-2">No templates yet.</p>
      <p className="text-[12px] text-gray-500 mb-5 max-w-sm mx-auto leading-relaxed">
        Save any wave as a template from its Control Room. Reuse the whole planning playbook — roles, bursts, KPI benchmarks — into a fresh draft with one click.
      </p>
      <button onClick={onPrimary}
              className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-[#FF4D94] text-white font-black text-[11px] uppercase tracking-wider hover:brightness-110 transition-all"
              style={{ boxShadow: '0 0 18px rgba(255,77,148,0.32)' }}
              data-testid="empty-state-cta">
        <Plus className="h-3 w-3" /> Go to my campaigns
      </button>
    </div>
  );
}

function ActionPill({ label, icon: Icon, onClick, busy, primary, danger, testId }) {
  // Per Design Contract addendum: row-level primaries on a list page do NOT
  // monopolise the pink filled-primary. They render as outlined-pink secondaries
  // so the single filled-pink CTA stays reserved for one page-level action.
  const styles = (() => {
    if (primary) {
      return {
        background: 'transparent',
        color: '#FF4D94',
        border: '1px solid rgba(255,77,148,0.45)',
      };
    }
    return {
      background: 'transparent',
      color: danger ? '#FF6B6B' : '#fff',
      border: `1px solid ${danger ? '#FF6B6B40' : '#1f1f1f'}`,
    };
  })();
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={busy}
      data-testid={testId}
      className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-full text-[10px] font-black uppercase tracking-wider transition-all hover:brightness-110 disabled:opacity-50"
      style={styles}
    >
      {busy ? <Loader2 className="h-3 w-3 animate-spin" /> : <Icon className="h-3 w-3" />}
      {label}
    </button>
  );
}
